import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { Calendar, MapPin, Users, ArrowRight, Pin } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1531058020387-3be344556be6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxleGVjdXRpdmUlMjBidXNpbmVzcyUyMG5ldHdvcmtpbmclMjBldmVudHxlbnwwfHx8fDE3ODI3NDg5MTR8MA&ixlib=rb-4.1.0&q=85";

export default function Info() {
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [ann, setAnn] = useState([]);
  useEffect(() => {
    api.get("/event").then(({ data }) => setEvent(data));
    api.get("/announcements").then(({ data }) => {
      const items = data.announcements || [];
      setAnn(items);
      // Fire read receipts for authenticated viewers. Backend is idempotent
      // per (announcement_id, user_id) — safe to call every render.
      if (user) {
        items.forEach((a) => {
          api.post(`/announcements/${a.id}/read`).catch(() => {});
        });
      }
    });
  }, [user]);

  return (
    <Layout>
      <section className="border-b border-[hsl(var(--line))]">
        <div className="container-summit py-16 md:py-20">
          <div className="overline mb-6"><span className="accent-rule" />Summit Details</div>
          <h1 className="text-4xl sm:text-6xl max-w-3xl">A one-day executive supplier engagement summit.</h1>
        </div>
      </section>
      <section className="container-summit py-12 md:py-16">
        <img src={HERO} alt="" className="w-full h-64 sm:h-96 object-cover grayscale" />
        <div className="mt-12 grid md:grid-cols-3 gap-px bg-[hsl(var(--line))]">
          <Tile icon={Calendar} title="Dates" body={event?.date || "—"} />
          <Tile icon={MapPin} title="Venue" body={event?.location || "—"} />
          <Tile icon={Users} title="Format" body="15 Prime Supplier stations · ~75–90 executive attendees" />
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl">What to expect</h2>
            <p className="mt-4 text-zinc-600 leading-relaxed">
              The Travel + Leisure Strategic Supplier Summit is a one-day private executive forum.
              You&apos;ll hear directly from procurement, sourcing, and category leaders through Educational
              Sessions and the Sourcing Fireside Chat, then engage with Prime Suppliers and Small
              Business Enterprises across the Prime Supplier Showcase &amp; Business Matchmaking afternoon.
            </p>
            <Link to="/schedule" className="btn-outline-black mt-8 inline-flex">View Schedule <ArrowRight size={16} /></Link>
          </div>
          <div>
            <h2 className="text-3xl">Announcements</h2>
            <div className="mt-4 grid gap-4">
              {ann.length === 0 && <p className="text-sm text-zinc-500">No announcements yet.</p>}
              {ann.map((a) => (
                <div key={a.id} className={`card-flat p-5 ${a.pinned ? "border-l-4 border-l-black" : ""}`} data-testid={`ann-view-${a.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="overline">{new Date(a.published_at || a.created_at).toLocaleDateString()}</span>
                    {a.priority === "high" && <span className="text-[10px] uppercase tracking-[0.18em] border px-1.5 py-0.5 border-red-300 text-red-700 bg-red-50">High</span>}
                    {a.pinned && <span className="text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1 border px-1.5 py-0.5 border-black text-black bg-white"><Pin size={10} /> Pinned</span>}
                  </div>
                  <div className="text-base font-medium mt-2">{a.title}</div>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed whitespace-pre-wrap">{a.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function Tile({ icon: Icon, title, body }) {
  return (
    <div className="bg-white p-8">
      <Icon size={20} className="text-[#A39171]" strokeWidth={1.4} />
      <div className="overline mt-4">{title}</div>
      <div className="serif text-2xl mt-2">{body}</div>
    </div>
  );
}
